import { AdElementVector, hasLocalVectorCoordinates } from '@adflow/types';
import type { ElementAnimationStyle } from '@adflow/utils';
import { FC, useEffect, useMemo } from 'react';

type Props = {
  data: AdElementVector['data'];
  position: AdElementVector['position'];
  size: AdElementVector['size'];
  name: AdElementVector['name'];
  svgIdPrefix?: string | null;
  animationStyle?: ElementAnimationStyle;
  animationTargetId?: string;
};

export function updateVectorTeamColors(
  el: Element,
  fillColor: string | null,
  strokeColor: string | null,
) {
  const shapes = el.querySelectorAll('path,circle,ellipse,polygon,rect');
  shapes.forEach((shape) => {
    const svgShape = shape instanceof SVGElement ? shape : null;
    const hasFill = shape.hasAttribute('fill') || Boolean(svgShape?.style.getPropertyValue('fill'));
    const hasStroke =
      shape.hasAttribute('stroke') || Boolean(svgShape?.style.getPropertyValue('stroke'));

    if (hasFill && fillColor) {
      shape.setAttribute('fill', fillColor);
      // Figma can export the same paint as an inline CSS declaration. Inline styles take
      // precedence over SVG presentation attributes, so update both representations.
      svgShape?.style.setProperty('fill', fillColor);
    }
    if (hasStroke && strokeColor) {
      shape.setAttribute('stroke', strokeColor);
      svgShape?.style.setProperty('stroke', strokeColor);
    }
    // remove fill opacity because fill and stroke support rgba, and overrides it
    shape.removeAttribute('fill-opacity');
    svgShape?.style.removeProperty('fill-opacity');
  });
}

const VectorElement: FC<Props> = ({
  data,
  position,
  size,
  name,
  svgIdPrefix,
  animationStyle,
  animationTargetId,
}) => {
  const svgId = name;
  const viewBoxWidth = data.viewBox.width;
  const viewBoxHeight = data.viewBox.height;
  const clipLeft = position.x ?? 0;
  const clipTop = position.y ?? 0;
  const clipWidth = size.width > 0 ? size.width : viewBoxWidth;
  const clipHeight = size.height > 0 ? size.height : viewBoxHeight;
  const content = useMemo(() => {
    return data.content.startsWith('https://cdn.fresh8.co') && data.viewBox.width
      ? `${data.content}?width=${data.viewBox.width * 2}`
      : data.content;
  }, [data.content, data.viewBox.width]);

  const isRootBackground = name.endsWith(' Background');
  // Figma content lives in frame coordinates and is shifted back by the element position.
  // A shape drawn in the editor is already in the element's own box, so it must not be.
  const isLocalShape = hasLocalVectorCoordinates(data.content);

  useEffect(() => {
    const strokeColor = data.color.stroke?.value || null;
    const fillColor = data.color.fill?.value || null;

    // Use prefixed ID if svgIdPrefix is provided, otherwise use the original svgId
    const prefixedId = svgIdPrefix ? `${svgIdPrefix}_${svgId}` : svgId;
    const svgElement = document.querySelector(
      '[id="' + prefixedId.replace(/["\\]/g, '\\$&').replace(/£/g, 'Â£') + '"]',
    );

    if (svgElement) {
      updateVectorTeamColors(svgElement, fillColor, strokeColor);
    }
  }, [svgIdPrefix, content, data, svgId]);

  const vectorSvg = (
    <svg
      viewBox={`0 0 ${viewBoxWidth} ${viewBoxHeight}`}
      width={viewBoxWidth}
      height={viewBoxHeight}
      fill="none" // It has to be none or the elements will be black
      id={svgId}
      style={
        isRootBackground
          ? undefined
          : {
              position: 'absolute',
              left: isLocalShape ? 0 : -clipLeft,
              top: isLocalShape ? 0 : -clipTop,
            }
      }
    >
      {data.effectFilters && <defs dangerouslySetInnerHTML={{ __html: data.effectFilters }}></defs>}
      <g
        dangerouslySetInnerHTML={{ __html: content }}
        type={'figma_vector'}
        id={svgId}
        fill={data.color.fill?.value}
        stroke={data.color.stroke?.value}
      ></g>
    </svg>
  );

  if (isRootBackground) {
    return (
      <div
        data-animation-target-id={animationTargetId}
        style={{
          position: 'absolute',
          left: 0,
          top: 0,
          zIndex: -1,
          width: viewBoxWidth,
          height: viewBoxHeight,
          transform: animationStyle?.transform,
          opacity: animationStyle?.opacity,
        }}
      >
        {vectorSvg}
      </div>
    );
  }

  return (
    <div
      data-animation-target-id={animationTargetId}
      style={{
        position: 'absolute',
        left: clipLeft,
        top: clipTop,
        zIndex: position.z,
        width: clipWidth,
        height: clipHeight,
        overflow: 'hidden',
        transform: animationStyle?.transform,
        opacity: animationStyle?.opacity,
      }}
    >
      {vectorSvg}
    </div>
  );
};

VectorElement.displayName = 'VectorElement';

export default VectorElement;
